import { useEffect, useMemo, useState } from 'react'
import type { Preset, PresetTake } from '@ultrix/shared'
import { useStore } from '../store'
import { listPresets, createPreset, updatePreset, deletePreset } from '../api'
import { PresetFireSheet } from '../components/PresetFireSheet'

type EditMode = null | { kind: 'new' } | { kind: 'edit'; preset: Preset }

export function Presets() {
  const user = useStore((s) => s.user)
  const sources = useStore((s) => s.sources)
  const destinations = useStore((s) => s.destinations)
  const favorites = useStore((s) => s.favorites)
  const state = useStore((s) => s.state)

  const isAdmin = user?.role === 'admin'

  const [presets, setPresets] = useState<Preset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<EditMode>(null)
  const [firing, setFiring] = useState<Preset | null>(null)

  const sourcesByNumber = useMemo(() => new Map(sources.map((s) => [s.number, s])), [sources])
  const destinationsByNumber = useMemo(() => new Map(destinations.map((d) => [d.number, d])), [destinations])

  async function refetch() {
    setLoading(true)
    setError(null)
    try {
      const list = await listPresets()
      setPresets(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refetch()
  }, [])

  async function onDelete(p: Preset) {
    if (!confirm(`Delete preset “${p.name}”?`)) return
    try {
      await deletePreset(p.id)
      await refetch()
    } catch (e) {
      alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  if (editing) {
    return (
      <PresetEditor
        mode={editing}
        sources={sources}
        destinations={destinations}
        sourcesByNumber={sourcesByNumber}
        destinationsByNumber={destinationsByNumber}
        favorites={favorites}
        state={state}
        onCancel={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null)
          await refetch()
        }}
      />
    )
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-4 py-3 border-b border-border flex items-center gap-3 shrink-0">
        <div className="text-lg font-semibold">Presets</div>
        <div className="flex-1" />
        {isAdmin && (
          <button
            onClick={() => setEditing({ kind: 'new' })}
            className="px-3 py-2 rounded-md bg-accent text-ink text-sm font-semibold"
          >
            + New Preset
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {loading && <div className="text-sub">Loading…</div>}
        {error && <div className="text-err">Error: {error}</div>}
        {!loading && !error && presets.length === 0 && (
          <div className="text-sub text-center py-10">No presets yet. An admin can create one.</div>
        )}
        {!loading && !error && presets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {presets.map((p) => (
              <div key={p.id} className="bg-panel border border-border rounded-lg p-3 flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink truncate">{p.name}</div>
                    <div className="text-xs text-sub">
                      {p.takes.length} route{p.takes.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditing({ kind: 'edit', preset: p })}
                        className="text-xs px-2 py-1 rounded border border-border bg-bg text-sub hover:text-ink"
                        aria-label="Edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(p)}
                        className="text-xs px-2 py-1 rounded border border-border bg-bg text-sub hover:text-err"
                        aria-label="Delete"
                      >
                        Del
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setFiring(p)}
                  className="px-3 py-2 rounded-md bg-accent text-ink font-semibold text-sm"
                >
                  Fire
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {firing && (
        <PresetFireSheet
          preset={firing}
          onClose={() => setFiring(null)}
          sourcesByNumber={sourcesByNumber}
          destinationsByNumber={destinationsByNumber}
        />
      )}
    </div>
  )
}

// ---------- Editor ----------

interface EditorProps {
  mode: Exclude<EditMode, null>
  sources: import('@ultrix/shared').Source[]
  destinations: import('@ultrix/shared').Destination[]
  sourcesByNumber: Map<number, import('@ultrix/shared').Source>
  destinationsByNumber: Map<number, import('@ultrix/shared').Destination>
  favorites: import('@ultrix/shared').Favorites
  state: Map<number, import('@ultrix/shared').CrosspointState>
  onCancel: () => void
  onSaved: () => void
}

function PresetEditor({
  mode,
  sources,
  destinations,
  sourcesByNumber,
  destinationsByNumber,
  favorites,
  state,
  onCancel,
  onSaved,
}: EditorProps) {
  const initial = mode.kind === 'edit' ? mode.preset : null
  const [name, setName] = useState(initial?.name ?? '')
  const [takes, setTakes] = useState<PresetTake[]>(initial?.takes ?? [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [pickDest, setPickDest] = useState<string>('')
  const [pickSource, setPickSource] = useState<string>('')
  const [destQuery, setDestQuery] = useState('')
  const [sourceQuery, setSourceQuery] = useState('')

  const sortedDests = useMemo(
    () => [...destinations].sort((a, b) => a.number - b.number),
    [destinations],
  )
  const sortedSources = useMemo(
    () => [...sources].sort((a, b) => a.number - b.number),
    [sources],
  )

  const filteredDests = useMemo(() => {
    const q = destQuery.trim().toLowerCase()
    if (!q) return sortedDests
    return sortedDests.filter(
      (d) => d.name.toLowerCase().includes(q) || String(d.number).includes(q),
    )
  }, [sortedDests, destQuery])

  const filteredSources = useMemo(() => {
    const q = sourceQuery.trim().toLowerCase()
    if (!q) return sortedSources
    return sortedSources.filter(
      (s) => s.name.toLowerCase().includes(q) || String(s.number).includes(q),
    )
  }, [sortedSources, sourceQuery])

  function snapshotFavorites() {
    const existingDests = new Set(takes.map((t) => t.destination))
    const additions: PresetTake[] = []
    for (const destNum of favorites.destinations) {
      if (existingDests.has(destNum)) continue
      const st = state.get(destNum)
      if (!st) continue
      additions.push({ destination: destNum, source: st.source, level: 0 })
    }
    if (additions.length === 0) return
    setTakes((prev) => [...prev, ...additions])
  }

  function addRoute() {
    const d = parseInt(pickDest, 10)
    const s = parseInt(pickSource, 10)
    if (!Number.isFinite(d) || !Number.isFinite(s)) return
    setTakes((prev) => {
      const filtered = prev.filter((t) => t.destination !== d)
      return [...filtered, { destination: d, source: s, level: 0 }]
    })
    setPickDest('')
    setPickSource('')
    setDestQuery('')
    setSourceQuery('')
  }

  function removeRoute(idx: number) {
    setTakes((prev) => prev.filter((_, i) => i !== idx))
  }

  async function onSave() {
    const trimmed = name.trim()
    if (trimmed.length < 1 || trimmed.length > 120) {
      setError('Name must be 1–120 characters.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (mode.kind === 'new') {
        await createPreset({ name: trimmed, takes })
      } else {
        await updatePreset(mode.preset.id, { name: trimmed, takes })
      }
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
        <button
          onClick={onCancel}
          className="px-3 py-2 rounded-md border border-border bg-panel text-ink text-sm hover:border-sub"
          disabled={saving}
        >
          Cancel
        </button>
        <div className="flex-1 text-lg font-semibold truncate">
          {mode.kind === 'new' ? 'New Preset' : `Edit: ${mode.preset.name}`}
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-accent text-ink text-sm font-semibold disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
        <div>
          <label className="block text-xs text-sub mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="Preset name"
            className="w-full px-3 py-2 rounded-md border border-border bg-panel text-ink outline-none focus:border-accent"
          />
        </div>

        {error && <div className="text-err text-sm">{error}</div>}

        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-semibold">Routes ({takes.length})</div>
            <div className="flex-1" />
            <button
              onClick={snapshotFavorites}
              className="px-3 py-1.5 rounded-md border border-border bg-panel text-sub hover:text-ink text-xs"
            >
              Snapshot favorites
            </button>
          </div>

          <div className="border border-border rounded-md bg-panel divide-y divide-border max-h-96 overflow-y-auto">
            {takes.length === 0 ? (
              <div className="p-3 text-sub text-sm">No routes yet.</div>
            ) : (
              takes.map((t, i) => {
                const destName = destinationsByNumber.get(t.destination)?.name ?? `#${t.destination}`
                const srcName = sourcesByNumber.get(t.source)?.name ?? `#${t.source}`
                return (
                  <div key={i} className="p-2 flex items-center gap-2 text-sm">
                    <span className="text-ink truncate flex-1">{destName}</span>
                    <span className="text-sub">←</span>
                    <span className="text-ink truncate flex-1">{srcName}</span>
                    <button
                      onClick={() => removeRoute(i)}
                      className="ml-2 text-xs px-2 py-1 rounded border border-border bg-bg text-sub hover:text-err"
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </div>
                )
              })
            )}
          </div>
        </div>

        <div className="border border-border rounded-md bg-panel p-3">
          <div className="text-sm font-semibold mb-2">Add Route</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-sub mb-1">Destination</div>
              <input
                type="text"
                value={destQuery}
                onChange={(e) => setDestQuery(e.target.value)}
                placeholder="Search destinations…"
                className="w-full mb-2 px-3 py-2 rounded-md border border-border bg-bg text-ink text-sm outline-none focus:border-accent"
              />
              <div className="max-h-56 overflow-y-auto border border-border rounded-md bg-bg divide-y divide-border">
                {filteredDests.length === 0 ? (
                  <div className="p-2 text-xs text-sub italic">No matches</div>
                ) : (
                  filteredDests.map((d) => {
                    const selected = pickDest === String(d.number)
                    return (
                      <button
                        key={d.number}
                        type="button"
                        onClick={() => setPickDest(String(d.number))}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 min-w-0 ${
                          selected ? 'bg-accent/20 text-ink' : 'hover:bg-panel text-ink'
                        }`}
                      >
                        <span className="truncate flex-1">{d.name}</span>
                        <span className="text-xs text-sub shrink-0">#{d.number}</span>
                      </button>
                    )
                  })
                )}
              </div>
            </div>

            <div>
              <div className="text-xs text-sub mb-1">Source</div>
              <input
                type="text"
                value={sourceQuery}
                onChange={(e) => setSourceQuery(e.target.value)}
                placeholder="Search sources…"
                className="w-full mb-2 px-3 py-2 rounded-md border border-border bg-bg text-ink text-sm outline-none focus:border-accent"
              />
              <div className="max-h-56 overflow-y-auto border border-border rounded-md bg-bg divide-y divide-border">
                {filteredSources.length === 0 ? (
                  <div className="p-2 text-xs text-sub italic">No matches</div>
                ) : (
                  filteredSources.map((s) => {
                    const selected = pickSource === String(s.number)
                    return (
                      <button
                        key={s.number}
                        type="button"
                        onClick={() => setPickSource(String(s.number))}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 min-w-0 ${
                          selected ? 'bg-accent/20 text-ink' : 'hover:bg-panel text-ink'
                        }`}
                      >
                        <span className="truncate flex-1">{s.name}</span>
                        <span className="text-xs text-sub shrink-0">#{s.number}</span>
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={addRoute}
              disabled={!pickDest || !pickSource}
              className="px-4 py-2 rounded-md bg-accent text-ink text-sm font-semibold disabled:opacity-40"
            >
              Add Route
            </button>
            <div className="text-xs text-sub flex-1 min-w-0 truncate">
              {pickDest && pickSource
                ? `${destinationsByNumber.get(parseInt(pickDest, 10))?.name ?? `#${pickDest}`} ← ${sourcesByNumber.get(parseInt(pickSource, 10))?.name ?? `#${pickSource}`}`
                : 'Pick a destination and source'}
            </div>
          </div>
          <div className="text-xs text-sub mt-2">
            Adding a route for an existing destination replaces its source.
          </div>
        </div>
      </div>
    </div>
  )
}
